import {
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Info,
  Loader2,
  MessageCircle,
  Phone,
  Shield,
  Sparkles,
  ThumbsUp,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import StepAssetsDebts from './StepAssetsDebts';

const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown || breakdown.length === 0) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <h4 className="font-semibold text-black mb-3 text-sm">Your Score Breakdown</h4>
      <div className="space-y-2">
        {breakdown.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <span className="text-gray-600 capitalize">{item.category.replace(/_/g, ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    item.score >= 70 ? 'bg-[#00D47E]' :
                    item.score >= 45 ? 'bg-yellow-500' : 'bg-red-400'
                  }`}
                  style={{ width: `${item.score}%` }}
                />
              </div>
              <span className="text-xs w-8 text-right font-medium">{item.score}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StepFinalReview = ({
  applicationId,
  approvalResult,
  contactSubmitted,
  contactSubmitting,
  formData,
  formatCurrency,
  handleAssetChange,
  handleChange,
  handleLiabilityChange,
  handleNoOtherDebtsChange,
  handleSpeakWithUs,
  handleSubmitApplication,
  loading,
  loanAmount,
  onReturnHome,
  scoringError,
  scoringErrorMessage,
  scoringInProgress,
  setShowSpeakWithUs,
  showSpeakWithUs,
}) => {
  if (contactSubmitted) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-20 h-20 bg-[#00D47E]/10 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-[#00D47E]" />
        </div>
        <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black">Thank You!</h2>
        <p className="text-gray-600 max-w-sm mx-auto">
          One of our finance specialists will contact you within 24 hours to discuss your options.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 text-left max-w-sm mx-auto">
          <h4 className="font-semibold text-sm mb-2">In the meantime:</h4>
          <ul className="text-sm text-gray-600 space-y-2">
            {approvalResult?.advice?.slice(0, 3).map((advice, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#00D47E] mt-0.5 shrink-0" />
                <span>{advice}</span>
              </li>
            ))}
          </ul>
        </div>
        <Button onClick={onReturnHome} variant="outline" className="mt-4">
          Return Home
        </Button>
      </div>
    );
  }

  return (
    <>
      {scoringInProgress && (
        <div className="space-y-6 text-center py-12">
          <div className="w-20 h-20 bg-[#00D47E]/10 rounded-full flex items-center justify-center mx-auto">
            <Loader2 className="w-10 h-10 text-[#00D47E] animate-spin" />
          </div>
          <div>
            <h2 className="font-outfit text-2xl font-bold text-black mb-2">
              Calculating your approval likelihood...
            </h2>
            <p className="text-gray-500 text-sm">
              Reviewing your income, employment, and financial position against NZ lender criteria
            </p>
          </div>
          <div className="flex justify-center gap-2">
            {['income_to_loan', 'debt_to_income', 'employment', 'affordability'].map((label, i) => (
              <div
                key={label}
                className="h-1.5 w-12 rounded-full bg-[#00D47E] animate-pulse"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
        </div>
      )}

      {scoringError && !approvalResult && !scoringInProgress && (
        <div className="space-y-6 text-center py-8">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <h2 className="font-outfit text-xl font-bold text-black mb-2">Something went wrong</h2>
            <p className="text-gray-500 text-sm max-w-xs mx-auto">
              We couldn&apos;t submit your details. Nothing was sent — your answers are still here,
              so please try again.
            </p>
            {/* The server's reason, inline. A toast the user misses leaves them retrying blind. */}
            {scoringErrorMessage && (
              <p className="text-sm text-red-600 max-w-xs mx-auto" data-testid="submission-error-detail">
                {scoringErrorMessage}
              </p>
            )}
          </div>
          <Button
            onClick={handleSubmitApplication}
            className="bg-[#00D47E] text-black hover:bg-[#00D47E]/90 rounded-full px-8"
          >
            Try Again
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Final step before submission: existing debts (they drive the DTI part of the score) and
          the consent the backend requires before it will store financial PII. */}
      {!approvalResult && !scoringInProgress && !scoringError && (
        <div className="space-y-6">
          <StepAssetsDebts
            formData={formData}
            handleAssetChange={handleAssetChange}
            handleLiabilityChange={handleLiabilityChange}
            handleNoOtherDebtsChange={handleNoOtherDebtsChange}
          />

          <div className="border-t border-gray-100 pt-6 space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => handleChange('consent', checked === true)}
                  className="mt-0.5"
                  data-testid="consent-checkbox"
                />
                <Label htmlFor="consent" className="text-sm text-gray-700 font-normal leading-relaxed cursor-pointer">
                  I agree to Levr storing the details I&apos;ve provided and having a finance
                  specialist contact me about my options, in line with the{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#00D47E] underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  .
                </Label>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                No credit check is run to see your result — this is an indicative assessment based
                on what you&apos;ve told us.
              </span>
            </div>
          </div>
        </div>
      )}

      {approvalResult && approvalResult.can_apply && !contactSubmitted && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#00D47E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ThumbsUp className="w-8 h-8 text-[#00D47E]" />
            </div>
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-2">
              {approvalResult.likelihood === 'high' ? 'Great News!' : 'Good Chances!'}
            </h2>
            <p className="text-gray-600">
              {approvalResult.likelihood === 'high'
                ? 'You have a high likelihood of approval'
                : 'You have a good chance of being approved'}
            </p>
          </div>

          <div className="flex justify-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
              approvalResult.likelihood === 'high'
                ? 'bg-[#00D47E]/10 text-[#00D47E]'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              <TrendingUp className="w-5 h-5" />
              <span className="font-semibold capitalize">{approvalResult.likelihood} Likelihood</span>
              <span className="text-sm opacity-75">({approvalResult.score}%)</span>
            </div>
          </div>

          <div className="bg-black rounded-2xl p-6 text-center">
            <p className="text-gray-400 text-sm mb-2">Estimated Weekly Payment</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-outfit text-2xl sm:text-4xl font-bold text-white">
                {approvalResult.estimated_weekly_min != null ? formatCurrency(approvalResult.estimated_weekly_min) : '—'}
              </span>
              <span className="text-gray-400">-</span>
              <span className="font-outfit text-2xl sm:text-4xl font-bold text-[#00D47E]">
                {approvalResult.estimated_weekly_max != null ? formatCurrency(approvalResult.estimated_weekly_max) : '—'}
              </span>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              {approvalResult.estimated_rate_min != null && approvalResult.estimated_rate_max != null
                ? `Based on ${approvalResult.estimated_rate_min}% - ${approvalResult.estimated_rate_max}% p.a.`
                : 'Rate subject to lender assessment'}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              For {formatCurrency(loanAmount)} over 60 months
            </p>
          </div>

          {/* The submission already happened — this screen reports it, it doesn't gate it. */}
          <div className="bg-[#00D47E]/5 border border-[#00D47E]/20 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#00D47E] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-black">Your details are with our team</p>
              <p className="text-sm text-gray-600 mt-1">
                A Levr finance specialist will be in touch to talk through your options — usually
                within 24 hours on a business day.
              </p>
              {applicationId && (
                <p className="text-xs text-gray-500 mt-2">
                  Reference <span className="font-mono">{applicationId}</span>
                </p>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold text-black mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-[#00D47E]" />
              What happens next?
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              {/* Describe specialist follow-up only. Nothing has gone to a lender: GTM v1
                  captures the lead and a human qualifies it before anything binding happens. */}
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#00D47E] mt-0.5 shrink-0" />
                <span>A specialist reviews your details and calls you</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#00D47E] mt-0.5 shrink-0" />
                <span>Together you work out which lenders suit your situation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#00D47E] mt-0.5 shrink-0" />
                <span>They confirm what you&apos;ll need before any application goes to a lender</span>
              </li>
            </ul>
          </div>

          <ScoreBreakdown breakdown={approvalResult.breakdown} />

          <p className="text-center text-xs text-gray-500">
            A credit check may be required by lenders as part of their formal assessment.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setShowSpeakWithUs(true)}
              variant="outline"
              className="flex-1 rounded-full"
              data-testid="speak-with-us-fallback-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Ask for a callback sooner
            </Button>
            <Button
              onClick={onReturnHome}
              className="flex-1 bg-[#00D47E] text-black hover:bg-[#00D47E]/90 rounded-full"
              data-testid="return-home-btn"
            >
              Done
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {approvalResult && !approvalResult.can_apply && !contactSubmitted && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-2">
              Let&apos;s Improve Your Chances
            </h2>
            <p className="text-gray-600">
              We&apos;ve got your details and a specialist will still call — here&apos;s how to
              boost your approval likelihood in the meantime
            </p>
          </div>

          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-100 text-yellow-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">Needs Improvement</span>
              <span className="text-sm opacity-75">({approvalResult.score}%)</span>
            </div>
          </div>

          {/* The lead was captured either way — say so, and give the same reference the
              high-likelihood branch does, so nobody thinks a low score means nothing was sent. */}
          <div className="bg-[#00D47E]/5 border border-[#00D47E]/20 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#00D47E] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-black">Your details are with our team</p>
              <p className="text-sm text-gray-600 mt-1">
                A specialist will still be in touch — a lower score often has options behind it.
              </p>
              {applicationId && (
                <p className="text-xs text-gray-500 mt-2">
                  Reference <span className="font-mono">{applicationId}</span>
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-black flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#00D47E]" />
              Tips to improve your chances:
            </h4>
            {approvalResult.advice?.map((advice, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-6 h-6 bg-[#00D47E]/10 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-[#00D47E]">{idx + 1}</span>
                </div>
                <p className="text-sm text-gray-700">{advice}</p>
              </div>
            ))}
          </div>

          <ScoreBreakdown breakdown={approvalResult.breakdown} />

          <div className="bg-black rounded-2xl p-6 text-center">
            <MessageCircle className="w-10 h-10 text-[#00D47E] mx-auto mb-3" />
            <h4 className="font-outfit text-xl font-bold text-white mb-2">Need Help?</h4>
            <p className="text-gray-400 text-sm mb-4">
              Our finance specialists can review your situation and find options you might not know about
            </p>
            <Button
              onClick={() => setShowSpeakWithUs(true)}
              className="bg-[#00D47E] text-black hover:bg-[#00D47E]/90 rounded-full px-8"
              data-testid="speak-with-us-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Speak with Us
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showSpeakWithUs} onOpenChange={setShowSpeakWithUs}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl font-bold">Get in Touch</DialogTitle>
            <DialogDescription>
              We&apos;ll call you to discuss your options. Our team specializes in finding solutions for all situations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {formData.name ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-medium">{formData.name}</p>
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Name *</label>
                <input
                  type="text"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D47E]/20 focus:border-[#00D47E]"
                  data-testid="speak-with-us-name"
                />
              </div>
            )}

            {formData.email ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium">{formData.email}</p>
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D47E]/20 focus:border-[#00D47E]"
                  data-testid="speak-with-us-email"
                />
              </div>
            )}

            {formData.phone ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Phone</p>
                <p className="font-medium">{formData.phone}</p>
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Phone *</label>
                <input
                  type="tel"
                  placeholder="021 123 4567"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D47E]/20 focus:border-[#00D47E]"
                  data-testid="speak-with-us-phone"
                />
              </div>
            )}

            <div className="bg-[#00D47E]/5 border border-[#00D47E]/20 rounded-lg p-3">
              <p className="text-xs text-gray-500">Loan Amount</p>
              <p className="font-semibold text-[#00D47E]">{formatCurrency(loanAmount)}</p>
            </div>
          </div>

          <Button
            onClick={handleSpeakWithUs}
            disabled={contactSubmitting || !formData.name || !formData.phone}
            className="w-full bg-[#00D47E] text-black hover:bg-[#00D47E]/90 h-12"
            data-testid="speak-with-us-submit"
          >
            {contactSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Request Callback
                <Phone className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">We typically respond within 24 hours</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StepFinalReview;
